import { Injectable } from '@angular/core';
import { ProductListFacade } from '@catalog/data-access';
import { select, Store } from '@ngrx/store';
import { CustomProduct, CustomProductScope } from '@product/const';
import { ReferenceActions, ReferenceSelectors, StateWithReference } from '@reference/data-access';
import { StateUtils } from '@spartacus/core';
import { Observable, of } from 'rxjs';
import { filter, map, mergeMap, take } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class RetailRocketProductReferenceService {
  constructor(
    private store: Store<StateWithReference>,
    private productListService: ProductListFacade,
  ) {}

  loadRetailRocketRecomendations(uid: string, productCodes: string[], type: string): void {
    this.store.dispatch(new ReferenceActions.LoadRetailRocketRecomendationList(uid, productCodes, type));

    this.getRetailRocketRecomendationsIdsState(uid)
      .pipe(
        filter((state) => !state.loading && !!state.success),
        take(1),
      )
      .subscribe((state) => {
        const codes = state.value;
        if (codes?.length) {
          this.loadProductsHandler(codes.map(String));
        }
      });
  }

  getRetailRocketRecomendationsIdsState(uid: string): Observable<StateUtils.LoaderState<number[]>> {
    return this.store.pipe(select(ReferenceSelectors.productRecomendationListState(uid)));
  }

  getRetailRocketRecomendationsIdsValue(uid: string): Observable<number[]> {
    return this.store.pipe(select(ReferenceSelectors.productRecomendationListValue(uid)));
  }

  getRetailRocketRecomendationsLoading(uid: string): Observable<boolean> {
    return this.store.pipe(select(ReferenceSelectors.productRecomendationListLoading(uid)));
  }

  getRetailRocketRecomendationsError(uid: string): Observable<boolean> {
    return this.store.pipe(select(ReferenceSelectors.productRecomendationListsError(uid)));
  }

  getRetailRocketRecomendationsSuccess(uid: string): Observable<boolean> {
    return this.store.pipe(select(ReferenceSelectors.productRecomendationListSuccess(uid)));
  }

  getRetailRocketRecomendations(uid: string, maximumNumberProducts: number): Observable<Observable<CustomProduct>[]> {
    return this.getRetailRocketRecomendationsIdsValue(uid).pipe(
      mergeMap((codes) => {
        return this.getProductsHandler(codes?.slice(0, maximumNumberProducts || 14));
      }),
    );
  }

  resetRetailRocketRecomendationList(uid: string): void {
    this.store.dispatch(new ReferenceActions.ResetRetailRocketRecomendationList(uid));
  }

  private loadProductsHandler(codes: string[]): void {
    if (codes?.length > 0) {
      this.productListService.loadProductsByCodes(codes, CustomProductScope.REFERENCE);
    }
  }

  private getProductsHandler(codes: number[]): Observable<Observable<CustomProduct>[] | undefined> {
    if (!codes) {
      return of(undefined);
    }

    return this.productListService.getSnapshotProductsByCodes(codes.map(String), CustomProductScope.REFERENCE).pipe(
      map((products: CustomProduct[]) => {
        return products.map((product: CustomProduct) => of(product));
      }),
    );
  }
}
